import { useQuery } from '@tanstack/react-query';
import Head from 'next/head';
import { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { FunctionComponent, useMemo } from 'react';

import usePageViewEvent from '../../../hooks/use-page-view-event';
import useHreflang from '../../../utils/seo/hreflang';
import useOGMeta from '../../../utils/seo/ogmeta';
import useSchema from '../../../utils/seo/schema-org/schema';
import { loadShippingLocations } from '../../modules/shipping-selector';
import { useAppContext } from '../../providers/app-context';

const ProductSchema: FunctionComponent<{
  product: TProductDetail;
  breadcrumbs: Array<{
    href: LinkProps;
    text: string;
  }>;
  translatedSlugs: any;
}> = ({ product, breadcrumbs, translatedSlugs }) => {
  const { locale = 'en', asPath, query } = useRouter();
  const { t } = useTranslation();
  const { detailedPrice, shippingLocation } = useAppContext();

  const { data: shippingLocationsOriginal } = useQuery(
    ['user-dependent', 'shipping-locations-list', locale, shippingLocation],
    (keys) => loadShippingLocations(keys)
  );

  const shippingLocations: (string | null)[] = useMemo(
    () =>
      shippingLocationsOriginal
        ? shippingLocationsOriginal
          ?.map((country) =>
            country.country_code !== 'XX' // remove: Others
              && !country.region_code.includes('.') // remove: UK and Islands
              ? country.country_code
              : null
          )
          .filter(Boolean)
        : [],
    [shippingLocationsOriginal]
  );

  const name = `${product.part.name} ${product.car.brand_name} ${product.car.model_name
    } ${product.car?.version_name ? product.car.version_name : ''} ${product.id
    } | B-Parts`;

  const description = t('product:head.description', {
    part: product.part.name,
    vehicle: `${product.car.brand_name} ${product.car.model_name} ${product.car?.version_name ? product.car.version_name : ''
      }`,
    id: product.id,
  });

  const localePath = locale !== 'en' ? `/${locale}` : '';

  usePageViewEvent(name);

  const og = useOGMeta({
    title: name,
    description,
    url: process.env.NEXT_PUBLIC_HOST + localePath + asPath,
    ...(product?.images.length && { image: product.images[0].image }),
  });

  const alternateLinks = useHreflang({
    locale,
    route: 'product',
    params: {
      category: query.category,
      subCategory: query.subCategory
    },
    translatedSlugs: translatedSlugs?.slugs,
  });

  const imagesForSchema = product.images.map(image => image.image_big).filter(image => image !== null);

  return (
    <Head>
      <title>{name}</title>
      <meta name="description" content={description} />
      {alternateLinks}
      {og}

      <script
        {...useSchema({
          name,
          description,
          breadcrumbs,
          extraSchema: [
            {
              '@type': 'Product',
              '@id': `${process.env.NEXT_PUBLIC_HOST}${localePath}${asPath}`,
              name: product.part.name,
              image: imagesForSchema.length > 0 ? imagesForSchema[0] : `${process.env.NEXT_PUBLIC_HOST}/img/schema-placeholder/no-image-${locale}.png`,
              description: `${product.part.name} ${product.car.brand_name} ${product.car.model_name
                } ${product.car?.version_name ? product.car.version_name : ''
                } - ${product.id}`,
              brand: { '@type': 'Brand', name: product.car.brand_name },
              sku: product.sku,
              ...(product.pricing && {
                offers: {
                  '@type': 'Offer',
                  url: `${process.env.NEXT_PUBLIC_HOST}${localePath}${asPath}`,
                  priceCurrency: product.pricing.currency,
                  price: detailedPrice
                    ? product?.pricing?.details?.product_price_in_currency
                    : product.pricing.price_final,
                  priceValidUntil: new Date().toISOString().split('T')[0],
                  availability:
                    product.status === 'SOL'
                      ? 'https://schema.org/OutOfStock'
                      : 'https://schema.org/InStock',
                  itemCondition: 'https://schema.org/UsedCondition',
                  seller: {
                    '@id': `${process.env.NEXT_PUBLIC_HOST}/#organization`,
                  },
                  ...(product.status !== 'SOL' && {
                    shippingDetails: {
                      '@type': 'OfferShippingDetails',
                      deliveryTime: {
                        '@type': 'ShippingDeliveryTime',
                        businessDays: [
                          'https://schema.org/Monday',
                          'https://schema.org/Tuesday',
                          'https://schema.org/Wednesday',
                          'https://schema.org/Thursday',
                          'https://schema.org/Friday'
                        ],
                        transitTime: {
                          '@type': 'QuantitativeValue',
                          minValue: product.delivery_day_start,
                          maxValue: product.delivery_day_end,
                          unitCode: 'DAY'
                        },
                        handlingTime: {
                          '@type': 'QuantitativeValue',
                          minValue: 0,
                          maxValue: 1,
                          unitCode: 'DAY'
                        },
                      },
                      deliveryMinDuration: {
                        '@type': 'QuantitativeValue',
                        minValue: product.delivery_day_start,
                        unitCode: 'DAY'
                      },
                      deliveryMaxDuration: {
                        '@type': 'QuantitativeValue',
                        maxValue: product.delivery_day_end,
                        unitCode: 'DAY'
                      },
                      shippingRate: {
                        '@type': 'MonetaryAmount',
                        value: detailedPrice
                          ? product?.pricing?.details?.shipping_in_currency
                          : '0',
                        currency: product.pricing.currency,
                      },
                      shippingDestination: shippingLocations?.map(
                        (country) => ({
                          '@type': 'DefinedRegion',
                          addressCountry: country,
                        })
                      ),
                    }
                  }),
                  ...(product.status !== 'SOL' && {
                    hasMerchantReturnPolicy: {
                      '@type': 'MerchantReturnPolicy',
                      returnFees: "https://schema.org/ReturnShippingFees",
                      refundType: "https://schema.org/FullRefund",
                      applicableCountry: shippingLocations,
                      returnPolicyCategory:
                        'https://schema.org/MerchantReturnFiniteReturnWindow',
                      merchantReturnDays: 14,
                      returnMethod: 'https://schema.org/ReturnByMail',
                      returnShippingFeesAmount: {
                        "@type": "MonetaryAmount",
                        currency: product.pricing.currency,
                        value: product.pricing.return_cost,
                      },
                      merchantReturnLink: `${process.env.NEXT_PUBLIC_HOST}${t('routes:terms-and-conditions')}#${t('terms:hash_return')}`,
                    }
                  }),
                },
              }),
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: '4,8',
                bestRating: 5,
                worstRating: 1,
                ratingCount: 8153,
                reviewCount: 8153,
              },
              review: [
                {
                  '@type': 'Review',
                  reviewBody:
                    'Fast shipping, great value for money and top customer support. Will definitely recommend!',
                  reviewRating: {
                    '@type': 'Rating',
                    ratingValue: '5',
                    bestRating: '5',
                    worstRating: '1',
                  },
                  datePublished: '2019-04-27',
                  author: {
                    '@type': 'Person',
                    name: 'Pedro Vieira',
                  },
                },
                {
                  '@type': 'Review',
                  reviewBody: 'Great user experience! Recommended!',
                  reviewRating: {
                    '@type': 'Rating',
                    ratingValue: '5',
                    bestRating: '5',
                    worstRating: '1',
                  },
                  datePublished: '2019-04-29',
                  author: {
                    '@type': 'Person',
                    name: 'Tiago Sá',
                  },
                },
                {
                  '@type': 'Review',
                  reviewBody:
                    'Their customer service is excellent! The process for ordering is very easy and fast. Very good value for money! I highly recommend this site!',
                  reviewRating: {
                    '@type': 'Rating',
                    ratingValue: '5',
                    bestRating: '5',
                    worstRating: '1',
                  },
                  datePublished: '2019-04-24',
                  author: {
                    '@type': 'Person',
                    name: 'Nathan Faigle',
                  },
                },
                {
                  '@type': 'Review',
                  reviewBody:
                    'bought a compressor for an Audi. Price 50% of the lowest price we had found. Delivered in two days in my office. The piece was spotless. Definitely became my first site to buy parts. Congratulations. TOP service',
                  reviewRating: {
                    '@type': 'Rating',
                    ratingValue: '5',
                    bestRating: '5',
                    worstRating: '1',
                  },
                  datePublished: '2018-05-30',
                  author: {
                    '@type': 'Person',
                    name: 'João Paulo Rodrigues',
                  },
                },
                {
                  '@type': 'Review',
                  reviewBody:
                    'I bought a door for BMW E34, top goods like I did not want rust, delivery super packed and fast.',
                  reviewRating: {
                    '@type': 'Rating',
                    ratingValue: '5',
                    bestRating: '5',
                    worstRating: '1',
                  },
                  datePublished: '2018-02-21',
                  author: {
                    '@type': 'Person',
                    name: 'Thomas Hermann',
                  },
                },
              ],
            },
          ],
        })}
      />
    </Head>
  );
};

export default ProductSchema;
